import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { loadRoute, queueSummary, saveRoute, setMeta } from './sync/db';
import { clearDriverSession, getDriverSession, saveDriverSession, DriverSession } from './sync/session';
import { bigButton, color, container, h1, muted, page } from './ui';

export interface DriverContext {
  session: DriverSession;
  /** True when the server could not be reached and the view runs from cache. */
  offline: boolean;
  /** Something the driver must be told, e.g. another person's uploads waiting. */
  notice: string;
}


/**
 * THE DRIVER VIEW'S OFFLINE START (Brad, 2026-09-14: a driver opening the app in
 * a dead zone must open to his cached route, never the login page).
 *
 * The portal's normal gate waits on the "who am I" call and treats any failure
 * as signed out. This gate does not wait: if this phone remembers who last
 * drove on it (session.ts - an id and a name, never a credential), it renders
 * that person's cached route immediately, and lets the check finish behind it.
 *
 *   signed in, can drive          -> render; remember who this is
 *   signed in, cannot drive       -> Access denied
 *   check still running / no network, and a remembered driver -> render offline
 *   server says signed out        -> if uploads are waiting, say "sign in to
 *                                    upload" (the queue is kept); else login
 *   no network and nobody remembered -> say so plainly
 *
 * Uploads are never unlocked by the remembered session: every request needs the
 * httpOnly cookie, and without one the queue keeps the stop and waits.
 */
export default function DriverGate({ children, next = '/delivery/driver' }: {
  children: (ctx: DriverContext) => React.ReactNode
  /** Where login returns to. The property walk shares this gate. */
  next?: string
}) {
  const LOGIN = `/login?next=${next}`;
  const { user, loading, authFailure, canAccessDeliveryPortal, canManageDelivery } = useAuth();
  const [session, setSession] = useState<DriverSession | null | undefined>(undefined);
  const [notice, setNotice] = useState('');
  const [pending, setPending] = useState(0);

  useEffect(() => {
    let alive = true;
    getDriverSession().then((s) => {
      if (alive) setSession(s ?? null);
    });
    queueSummary().then((q) => {
      if (alive) setPending(q.pendingStops);
    });
    return () => {
      alive = false;
    };
  }, []);

  // A live, authorised sign-in: remember who this is. If it is a DIFFERENT
  // person from the one this phone remembered, the cached route was theirs -
  // drop it (the server will send this person's) - and if their uploads are
  // still waiting, say whose they are. The queue itself is never touched.
  useEffect(() => {
    if (!user || !canAccessDeliveryPortal || session === undefined) return;
    if (session && session.userId === user.id) return;
    (async () => {
      const previous = session;
      if (previous && previous.userId !== user.id) {
        const q = await queueSummary();
        if (q.pendingStops > 0) {
          setNotice(`${q.pendingStops} stop(s) from ${previous.name} are still waiting to upload. ` +
            `They upload only when ${previous.name} signs in on this phone.`);
        }
        if (await loadRoute()) await saveRoute(null);
        await setMeta(`order:${previous.userId}`, undefined);
      }
      const name = user.first_name ? `${user.first_name} ${user.last_name}`.trim() : user.username;
      setSession(await saveDriverSession({
        userId: user.id, username: user.username, name, canManage: canManageDelivery,
      }));
    })();
  }, [user, canAccessDeliveryPortal, canManageDelivery, session]);

  if (session === undefined) {
    return <div style={page} />;   // an IndexedDB read - milliseconds, not a network wait
  }

  if (user && !canAccessDeliveryPortal) {
    return (
      <div style={page}>
        <div style={container}>
          <div style={h1}>No delivery access</div>
          <div style={muted}>This account cannot drive routes. Ask a manager to set your delivery role.</div>
        </div>
      </div>
    );
  }

  if (user && canAccessDeliveryPortal) {
    const live: DriverSession = session && session.userId === user.id ? session : {
      userId: user.id, username: user.username,
      name: user.first_name ? `${user.first_name} ${user.last_name}`.trim() : user.username,
      canManage: canManageDelivery, savedAt: Date.now(),
    };
    return <>{children({ session: live, offline: false, notice })}</>;
  }

  if (session && (loading || authFailure === 'network')) {
    return <>{children({ session, offline: !loading, notice })}</>;
  }

  if (!loading && authFailure === 'unauthenticated') {
    if (session && pending > 0) {
      return (
        <div style={page}>
          <div style={container}>
            <div style={h1}>Sign in to upload</div>
            <div style={{ ...muted, marginBottom: 16 }}>
              {pending} stop{pending === 1 ? '' : 's'} from {session.name} {pending === 1 ? 'is' : 'are'} saved on this
              phone and waiting to upload. Nothing is lost. Sign in as {session.name} to send them.
            </div>
            <a href={LOGIN} style={{ ...bigButton(color.green), textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box' }}>
              Sign in
            </a>
          </div>
        </div>
      );
    }
    if (session) void clearDriverSession();
    return <Navigate to={LOGIN} replace />;
  }

  if (loading) {
    return (
      <div style={page}>
        <div style={container}>
          <div style={h1}>Starting…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <div style={container}>
        <div style={h1}>No signal</div>
        <div style={muted}>
          This phone has not opened a route yet, so there is nothing saved to show. Open the route once
          with signal before heading out.
        </div>
      </div>
    </div>
  );
}
